import React, {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import {
  getTableMenuFixedPosition,
  TABLE_MENU_DROPDOWN_CLASS,
  type TableMenuAlign,
} from './tableMenuUtils';

type TableMenuPortalProps = {
  isOpen: boolean;
  anchorRef?: RefObject<HTMLElement | null>;
  anchorElement?: HTMLElement | null;
  menuRef?: Ref<HTMLDivElement | null>;
  align?: TableMenuAlign;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'data-item-id'?: string | number;
  children: ReactNode;
};

function assignRef<T>(ref: Ref<T> | undefined, value: T): void {
  if (!ref) {
    return;
  }
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  ref.current = value;
}

const TableMenuPortal: React.FC<TableMenuPortalProps> = ({
  isOpen,
  anchorRef,
  anchorElement,
  menuRef,
  align = 'left',
  id,
  className = 'w-40',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-item-id': dataItemId,
  children,
}) => {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const internalMenuRef = useRef<HTMLDivElement | null>(null);

  const setMenuNode = (element: HTMLDivElement | null) => {
    internalMenuRef.current = element;
    assignRef(menuRef, element);
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }

    const resolveAnchor = () => anchorRef?.current ?? anchorElement ?? null;

    const updatePosition = () => {
      const anchor = resolveAnchor();
      if (!anchor) {
        return;
      }

      const menuEl = internalMenuRef.current;
      setPosition(
        getTableMenuFixedPosition(anchor, {
          align,
          menuWidth: menuEl?.offsetWidth || 160,
          menuHeight: menuEl?.offsetHeight || 160,
        }),
      );
    };

    updatePosition();
    const rafId = window.requestAnimationFrame(updatePosition);

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [align, anchorElement, anchorRef, isOpen]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const style = position ?? { top: 0, left: 0, visibility: 'hidden' as const };

  return createPortal(
    <div
      id={id}
      ref={setMenuNode}
      role="menu"
      className={`${TABLE_MENU_DROPDOWN_CLASS} ${className}`}
      style={style}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      data-item-id={dataItemId}
    >
      {children}
    </div>,
    document.body,
  );
};

export default TableMenuPortal;
